import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Tooltip,
    Alert,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';

const PROMOTION_TYPES = [
    'DEAL', 'SMART', 'PRICE_DISCOUNT', 'VOLUME', 'PRE_NEGOTIATED', 
    'DOD', 'LIGHTNING', 'SELLER_CAMPAIGN', 'PRICE_MATCHING', 
    'UNHEALTHY_STOCK', 'SELLER_COUPON_CAMPAIGN'
];

const PROMOTION_TYPES_LABELS = {
    'DEAL': 'Oferta',
    'SMART': 'Inteligente',
    'PRICE_DISCOUNT': 'Desconto de Preço',
    'VOLUME': 'Volume',
    'PRE_NEGOTIATED': 'Pré-negociado',
    'DOD': 'Oferta do Dia',
    'LIGHTNING': 'Relâmpago',
    'SELLER_CAMPAIGN': 'Campanha do Vendedor',
    'PRICE_MATCHING': 'Equiparação de Preço',
    'UNHEALTHY_STOCK': 'Estoque Problemático',
    'SELLER_COUPON_CAMPAIGN': 'Campanha de Cupom do Vendedor'
};

export default function TriggerList({ 
    triggers, 
    totalTriggers,
    triggerSettingsCounts,
    filters,
    onFiltersChange,
    onCreateTrigger,
    onEditTrigger,
    onDeleteTrigger,
    onViewSettings,
    isLoading 
}) {
    const formatDate = (dateString) => {
        if (!dateString) return 'Indefinido';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    return (
        <Box>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 4,
                p: 3,
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(255, 152, 0, 0.08)',
                border: '1px solid #fff3e0'
            }}>
                <Box>
                    <Typography variant="h5" sx={{ 
                        color: '#ff9800',
                        fontWeight: 700,
                        mb: 0.5
                    }}>
                        Gatilhos de Promoção
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {totalTriggers} gatilho(s) encontrado(s)
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onCreateTrigger}
                    sx={{ 
                        textTransform: 'none',
                        bgcolor: '#ff9800',
                        color: 'white',
                        fontWeight: 600,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': { 
                            bgcolor: '#f57c00',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(255, 152, 0, 0.3)'
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    Novo Gatilho
                </Button>
            </Box>

            <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                gap: 2, 
                flexWrap: 'wrap',
                p: 3,
                bgcolor: 'white',
                borderRadius: 3,
                border: '1px solid #f0f0f0'
            }}>
                <Box sx={{ minWidth: 200 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Filtrar por Status</InputLabel>
                        <Select
                            value={filters.active ?? ''}
                            label="Filtrar por Status"
                            onChange={(e) => onFiltersChange({
                                ...filters, 
                                active: e.target.value === '' ? null : e.target.value
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#ff9800'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#ff9800'
                                    }
                                }
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value={true}>Ativo</MenuItem>
                            <MenuItem value={false}>Inativo</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ minWidth: 200 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Tipo de Promoção</InputLabel>
                        <Select
                            value={filters.promotion_type}
                            label="Tipo de Promoção"
                            onChange={(e) => onFiltersChange({
                                ...filters, 
                                promotion_type: e.target.value
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#ff9800'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#ff9800'
                                    }
                                }
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {PROMOTION_TYPES.map(type => (
                                <MenuItem key={type} value={type}>
                                    {PROMOTION_TYPES_LABELS[type] || type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {isLoading ? (
                <Box sx={{ 
                    textAlign: 'center', 
                    py: 8,
                    bgcolor: 'white',
                    borderRadius: 3,
                    border: '1px solid #f0f0f0'
                }}>
                    <Typography color="text.secondary">Carregando gatilhos...</Typography>
                </Box>
            ) : triggers.length === 0 ? (
                <Alert 
                    severity="info" 
                    sx={{ 
                        textAlign: 'center',
                        borderRadius: 3,
                        border: '1px solid #e3f2fd',
                        bgcolor: '#f8f9ff',
                        py: 4
                    }}
                >
                    <Typography sx={{ fontWeight: 500, mb: 1 }}>
                        Nenhum gatilho encontrado
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Clique em "Novo Gatilho" para criar o primeiro.
                    </Typography>
                </Alert>
            ) : (
                <TableContainer 
                    component={Paper} 
                    sx={{ 
                        borderRadius: 3,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                        border: '1px solid #f0f0f0',
                        overflow: 'hidden'
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#fff8f0' }}>
                                <TableCell sx={{ 
                                    fontWeight: 700,
                                    color: '#ff9800',
                                    fontSize: '0.95rem'
                                }}>
                                    Tipo
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 700,
                                    color: '#ff9800',
                                    fontSize: '0.95rem'
                                }}>
                                    Status
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 700,
                                    color: '#ff9800',
                                    fontSize: '0.95rem'
                                }}>
                                    Regras
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 700,
                                    color: '#ff9800',
                                    fontSize: '0.95rem'
                                }}>
                                    Settings
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 700,
                                    color: '#ff9800',
                                    fontSize: '0.95rem'
                                }}>
                                    Expira em
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 700,
                                    color: '#ff9800',
                                    fontSize: '0.95rem'
                                }}>
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {triggers.map((trigger) => {
                                return (
                                <TableRow 
                                    key={trigger._id}
                                    sx={{
                                        '&:hover': { 
                                            bgcolor: 'rgba(255, 152, 0, 0.04)' 
                                        },
                                        '&:nth-of-type(even)': {
                                            bgcolor: '#fafafa'
                                        }
                                    }}
                                >
                                    <TableCell>
                                        <Chip 
                                            label={trigger.promotion_type}
                                            size="small"
                                            sx={{ 
                                                bgcolor: '#e3f2fd', 
                                                color: '#1976d2',
                                                fontWeight: 500
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={trigger.active ? 'Ativo' : 'Inativo'}
                                            size="small"
                                            sx={{
                                                bgcolor: trigger.active ? '#e8f5e8' : '#fff3e0',
                                                color: trigger.active ? '#2e7d32' : '#f57c00',
                                                fontWeight: 600
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{
                                            bgcolor: '#f5f5f5',
                                            color: '#666',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: 2,
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            display: 'inline-block'
                                        }}>
                                            {trigger.trigger_rules?.length || 0} regra(s)
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const realCount = triggerSettingsCounts[trigger._id];
                                            const isLoading = realCount === undefined;
                                            const count = realCount || 0;
                                            
                                            return (
                                                <Chip 
                                                    label={isLoading 
                                                        ? 'Carregando...' 
                                                        : `${count} produto(s)`
                                                    }
                                                    size="small"
                                                    sx={{ 
                                                        bgcolor: count > 0 ? '#e8f5e8' : '#fff3e0', 
                                                        color: count > 0 ? '#2e7d32' : '#f57c00',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ 
                                            color: '#666',
                                            fontSize: '0.875rem'
                                        }}>
                                            {formatDate(trigger.expire_in)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Ver Settings">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => onViewSettings(trigger)}
                                                    sx={{ 
                                                        color: '#2196f3',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(33, 150, 243, 0.1)',
                                                            transform: 'scale(1.1)'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <SettingsIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => onEditTrigger(trigger)}
                                                    sx={{ 
                                                        color: '#ff9800',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(255, 152, 0, 0.1)',
                                                            transform: 'scale(1.1)'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Deletar">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => onDeleteTrigger(trigger._id)}
                                                    sx={{ 
                                                        color: '#f44336',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                                                            transform: 'scale(1.1)'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}